import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import zodiacImage from '../../assets/zodiac-wheel.svg';

const ZodiacWheel = () => {
  const wheelRef = useRef(null);
  const outerRingRef = useRef(null);
  const glowRef = useRef(null);
  const containerRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const wheel = wheelRef.current;
    const outerRing = outerRingRef.current;
    const glow = glowRef.current;
    const container = containerRef.current;

    // Initial state - hidden for cinematic entrance
    gsap.set(container, { opacity: 0, scale: 0.8 });
    gsap.set(wheel, { rotation: 0 });
    gsap.set(outerRing, { rotation: 0 });

    // Cinematic entrance animation
    const entranceTl = gsap.timeline({ delay: 0.5 });
    
    entranceTl
      .to(container, {
        opacity: 1,
        scale: 1,
        duration: 2.5,
        ease: 'power2.out',
      })
      .to(glow, {
        opacity: 0.6,
        duration: 2,
        ease: 'power2.inOut',
      }, '-=2');

    // Infinite wheel rotation - RIGHT TO LEFT (negative = clockwise visual)
    const wheelRotation = gsap.timeline({ repeat: -1 });
    wheelRotation.to(wheel, {
      rotation: -360,
      duration: 40,
      ease: 'none',
      modifiers: {
        rotation: (value) => {
          // Add subtle organic variation
          const variation = Math.sin(Date.now() * 0.0002) * 1.5;
          return parseFloat(value) + variation;
        }
      }
    });

    // Outer ring - FASTER, same direction (right to left)
    const outerRotation = gsap.timeline({ repeat: -1 });
    outerRotation.to(outerRing, {
      rotation: -360,
      duration: 25,
      ease: 'none',
    });

    // Breathing scale animation (±2%)
    gsap.to(container, {
      scale: 1.02,
      duration: 8,
      ease: 'power1.inOut',
      yoyo: true,
      repeat: -1,
    });

    // Synchronized glow pulsation
    gsap.to(glow, {
      opacity: 1,
      scale: 1.1,
      duration: 4,
      ease: 'power1.inOut',
      yoyo: true,
      repeat: -1,
    });

    // Scroll parallax effect
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      wheelRotation.kill();
      outerRotation.kill();
      entranceTl.kill();
    };
  }, []);

  // Apply micro-parallax based on scroll
  const parallaxOffset = scrollY * 0.05;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center pointer-events-none z-0"
      style={{ perspective: '1000px' }}
    >
      <div
        ref={containerRef}
        className="relative"
        style={{
          transform: `translateY(${parallaxOffset}px) translateZ(0)`,
          willChange: 'transform, opacity',
        }}
      >
        {/* Cosmic Glow Layer */}
        <div
          ref={glowRef}
          className="absolute inset-0 rounded-full opacity-0"
          style={{
            width: 'min(80vw, 80vh)',
            height: 'min(80vw, 80vh)',
            background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, rgba(212,175,55,0.1) 40%, transparent 70%)',
            filter: 'blur(40px)',
            transform: 'translate(-50%, -50%) scale(1.3)',
            left: '50%',
            top: '50%',
          }}
        />

        {/* Outer Halo Ring (Counter-rotating) */}
        <div
          ref={outerRingRef}
          className="absolute rounded-full"
          style={{
            width: 'min(85vw, 85vh)',
            height: 'min(85vw, 85vh)',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'transparent',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            boxShadow: '0 0 60px rgba(212, 175, 55, 0.1), inset 0 0 60px rgba(212, 175, 55, 0.05)',
            willChange: 'transform',
          }}
        >
          {/* Decorative dots on outer ring */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-cosmic-gold"
              style={{
                left: '50%',
                top: '0',
                transform: `rotate(${i * 30}deg) translateY(-50%) translateX(-50%)`,
                transformOrigin: '50% min(42.5vw, 42.5vh)',
                opacity: 0.6,
                boxShadow: '0 0 10px rgba(212, 175, 55, 0.8)',
              }}
            />
          ))}
        </div>

        {/* Main Zodiac Wheel */}
        <div
          ref={wheelRef}
          className="relative"
          style={{
            width: 'min(70vw, 70vh)',
            height: 'min(70vw, 70vh)',
            willChange: 'transform',
          }}
        >
          <img
            src={zodiacImage}
            alt="Zodiac Wheel"
            className="w-full h-full object-contain"
            style={{
              filter: 'drop-shadow(0 0 30px rgba(212, 175, 55, 0.5))',
            }}
            draggable={false}
          />
        </div>

        {/* Inner Glow Core */}
        <div
          className="absolute rounded-full"
          style={{
            width: 'min(20vw, 20vh)',
            height: 'min(20vw, 20vh)',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(212,175,55,0.3) 0%, transparent 70%)',
            filter: 'blur(20px)',
            animation: 'pulse 4s ease-in-out infinite',
          }}
        />
      </div>
    </div>
  );
};

export default ZodiacWheel;
